"use client";

import React, { useEffect, useState } from 'react';
import { useUserSettingsContext } from "../../utils/context";
import Loader from "../loader/loader"; // Import the Loader component
import { createViewerPass, getViewerPass, SignInMode } from '../../utils/clipApi';
import { resolveHost } from '../../shows';
import { keepCodeStep, readCodeStep } from './codeStep';
import './gate.scss';

// The credential is checked by the API (POST /viewer-pass), which answers with an HttpOnly cookie, and the
// clip, its frames and its product photos are served only to a browser holding it: a copied video address
// opens nothing. Nothing about the passcode is in this bundle in production.
//
// Two forms (SIL-1595). On a show's own host whose config says `signIn: 'email'` (pll.silvr.shop) the gate
// asks for a work email and posts {email}; the API lets the address in when its domain is on that site's
// allowed list and neither it nor its domain is blocked, and logs the attempt. Everywhere else (the board
// on dev.silvr.shop, localhost, a show without the flag) it asks for the shared viewer passcode as before.
// The API decides the mode for real from the request's Origin (its viewer_sites table); the config only
// picks the form shown first, and the answer to GET /viewer-pass corrects it, so a show flagged email on
// a deployment that has no row for it gets a working passcode form rather than a dead one.
//
// A site may also ask for a code emailed to the address (SIL-1618: the dev board, six named people). The
// API says so in GET /viewer-pass (`code: true`); the email then answers `code_sent` instead of a pass, the
// form asks for the code Supabase emailed, and the email with the code gets the pass.
//
// VITE_APP_GATE_PASSCODE is the fallback for a deployment with no such endpoint (the ML box's interim
// host, local work): the comparison happens here and the media is open, which was the old behaviour
// everywhere. Leave it unset in production. It only ever applies to the passcode form.
const GATE_PASSCODE = import.meta.env.VITE_APP_GATE_PASSCODE;

type Copy = { subtitle: string; label: string; placeholder: string; button: string };

const COPY: Record<SignInMode, Copy> = {
  passcode: { subtitle: 'Enter your passcode to continue your journey.', label: 'Passcode', placeholder: '••••••••', button: 'Unlock Silvr' },
  email: { subtitle: 'Enter your work email to watch the demo.', label: 'Work email', placeholder: 'you@company.com', button: 'Continue' },
};
/** The email form on a site that asks for a code, and the code form after it. */
const CODE_EMAIL_COPY: Copy = { subtitle: "Enter your Silvr email and we'll send you a sign-in code.", label: 'Email', placeholder: 'you@silvr.ai', button: 'Send code' };
const codeCopy = (email: string): Copy => ({ subtitle: `We sent a code to ${email}. Enter it to sign in.`, label: 'Code', placeholder: '123456', button: 'Sign in' });

/** The form this host shows first: email on a show host flagged for it, the passcode anywhere else. */
export function configuredSignInMode(): SignInMode {
  const host = resolveHost();
  return host.mode === 'show' && host.show.signIn === 'email' ? 'email' : 'passcode';
}

const Gate: React.FC = () => {
  const [mode, setMode] = useState<SignInMode>(configuredSignInMode);
  const [value, setValue] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Loader state
  const [error, setError] = useState(''); // Error state
  /** The site asks for an emailed code after the email (from GET /viewer-pass). */
  const [wantsCode, setWantsCode] = useState(false);
  /** The address a code was sent to: set, the form asks for the code. Kept for the tab, so a reload or the
   *  email opened in the same tab comes back to the code step (./codeStep.ts). */
  const [codeFor, setCodeForState] = useState<string>(() => readCodeStep());
  const [notice, setNotice] = useState('');
  const { setUserSettings } = useUserSettingsContext();
  const isLocalhost = window.location.hostname === 'localhost';
  const askingCode = mode === 'email' && codeFor !== '';
  const copy = askingCode ? codeCopy(codeFor) : mode === 'email' && wantsCode ? CODE_EMAIL_COPY : COPY[mode];

  const setCodeFor = (email: string) => {
    setCodeForState(email);
    keepCodeStep(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setError('');
    setNotice('');
  };

  // A pass from earlier is enough: do not ask again. (Also covers a deployment with the pass switched
  // off, which answers required: false.) The same answer says how this site signs in.
  useEffect(() => {
    if (isLocalhost || (GATE_PASSCODE && mode === 'passcode')) return;
    let cancelled = false;
    getViewerPass()
      .then((state) => {
        if (cancelled) return;
        if (state.mode === 'email' || state.mode === 'passcode') setMode(state.mode);
        const code = state.mode === 'email' && state.code === true;
        setWantsCode(code);
        // A code step kept from before the reload only stands where the site still asks for a code and
        // the browser has not already signed in.
        if (!code || state.ok) setCodeFor('');
        if (state.ok) letIn();
      })
      .catch(() => {
        /* no endpoint, or offline: fall through to the form */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLocalhost) {
      setIsLoading(true); // Show loader
      setTimeout(() => {
        setUserSettings(prev => ({
          ...prev,
          isLoggedIn: true,
        }));
      }, 3000);
    }
  }, [isLocalhost, setUserSettings]);

  const letIn = () => {
    setIsLoading(true); // Show loader
    setTimeout(() => {
      setUserSettings(prev => ({
        ...prev,
        isLoggedIn: true,
      }));
    }, 3000); // Redirect after 3 seconds
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'passcode' && GATE_PASSCODE) {
      if (value === GATE_PASSCODE) letIn();
      else setError('❌ Incorrect Passcode. Access denied.');
      return;
    }
    setError('');
    setNotice('');
    try {
      const credential = mode === 'passcode' ? { passcode: value }
        : askingCode ? { email: codeFor, code: value.trim() }
        : { email: value.trim() };
      const state = await createViewerPass(credential);   // sets the pass cookie, or throws
      if (state.code_sent) {
        // A site that asks for a code: the API had one emailed, and the pass waits for it.
        setWantsCode(true);
        setCodeFor(state.email || value.trim());
        setValue('');
        return;
      }
      setCodeFor('');
      letIn();
    } catch (err) {
      // The email and code forms show the API's plain message as is ("That address can't open this demo. Use
      // your company email, or ask Silvr for access.", "That code is wrong or has expired..."); the passcode
      // form keeps its old wording.
      setError(mode === 'email' ? (err as Error).message : `❌ ${(err as Error).message}. Access denied.`);
    }
  };

  /** Ask for another code for the same address (the API refuses a second one inside a minute, and says so). */
  const resendCode = async () => {
    setError('');
    setNotice('');
    try {
      await createViewerPass({ email: codeFor });
      setCodeFor(codeFor);   // a fresh code: the hour starts again
      setValue('');
      setNotice(`A new code is on its way to ${codeFor}.`);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const useAnotherEmail = () => {
    setCodeFor('');
    setValue('');
    setError('');
    setNotice('');
  };

  if (isLoading) {
    return <Loader />; // Show loader while loading
  }

  return (
    <div className="gate-wrapper">
      <div className="bg-layers" />
      <div className="gate-form-container fade-in">
        <form className="glass-form" onSubmit={handleLogin}>
          <img src="/silvr-logo.png" alt="Silvr" className="logo" />
          <h1>Welcome Back</h1>
          <p className="subtitle">{copy.subtitle}</p>
          <div className="field">
            <label htmlFor="gate-credential">{copy.label}</label>
            <div className="input-wrap">
              {askingCode ? (
                <input
                  key="code"
                  id="gate-credential"
                  name="code"
                  type="text"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  value={value}
                  onChange={handleChange}
                  placeholder={copy.placeholder}
                  required
                  autoFocus
                />
              ) : (
                <input
                  key="credential"
                  id="gate-credential"
                  name={mode === 'email' ? 'email' : 'passcode'}
                  type={mode === 'email' ? 'email' : 'password'}
                  autoComplete={mode === 'email' ? 'email' : 'current-password'}
                  inputMode={mode === 'email' ? 'email' : undefined}
                  value={value}
                  onChange={handleChange}
                  placeholder={copy.placeholder}
                  required
                />
              )}
              {/* <span className="icon">🔒</span> */}
            </div>
            {error && <div role="alert" style={{ color: mode === 'email' ? '#fecaca' : 'red', marginTop: '0.5rem', fontWeight: 600 }}>{error}</div>}
            {notice && <div role="status" style={{ color: '#bbf7d0', marginTop: '0.5rem', fontWeight: 600 }}>{notice}</div>}
          </div>
          <button className="submit-btn" type="submit">{copy.button}</button>
          {askingCode && (
            <div className="gate-code-links">
              <button type="button" className="gate-link" onClick={() => void resendCode()}>Send a new code</button>
              <button type="button" className="gate-link" onClick={useAnotherEmail}>Use a different email</button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default React.memo(Gate);
